// Sugalarm — Cycle 234: "Connect to CGM" modal for Settings → CGM &
// Connection (the active member's connection card).
//
// History / the gap this closes: before cycle 234 the ConnectionSection
// "Connect a CGM" / "Reconnect" buttons both called `onReconnect`, which
// was wired to `nav("/login")` in SettingsScreen — a dead-end for an
// authenticated session (it bounced to the sign-in splash and could not
// complete a fresh CGM link, so a user who disconnected to switch
// providers got stuck). This modal surfaces the existing
// ProviderPicker → CGMConnectForm flow (already used per-member in the
// Members pane + at login) right in Settings instead.
//
// Two steps, always starting at the provider picker with NO pre-selection
// (the parent gates the mount, so the modal remounts fresh on each open —
// a prior provider is never carried across open/close cycles):
//   (1) ProviderPicker — Dexcom / Libre.
//   (2) CGMConnectForm in "connect" (fresh-credential) mode for the chosen
//       provider, submitting through the default active-member
//       `cgmApi.connect` path.
//
// Modal pattern: the cycle-34.20 scale-in + blurred backdrop, role="dialog"
// + aria-modal, focus moved in on open and returned to the trigger on
// close, Tab trapped within the panel, Escape / ✕ / tap-outside dismiss.
// Kiosk on-screen keyboard: the cycle-88 CgmAuthModal pattern — when a
// field is focused the panel anchors to the top, reserves the keyboard's
// footprint (`padding-bottom`), and scrolls the focused field into view.

import { useEffect, useRef, useState } from "react";

import * as cgmApi from "../../api/cgm";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { CGMConnectForm } from "../login/CGMConnectForm";
import { ProviderPicker } from "../login/ProviderPicker";
import type { Provider } from "../login/providers";
import styles from "./CgmConnectModal.module.css";

interface Props {
  variant: FrameVariant;
  /** Cancel (✕ / Escape / tap-outside) — close with no state change. */
  onClose: () => void;
  /** Fired with the connect result on a successful fresh link. The parent
   *  refreshes CGM status, shows the toast, and refreshes the active member. */
  onConnected: (result: cgmApi.CGMConnectResult) => void;
}

export function CgmConnectModal({ variant, onClose, onConnected }: Props) {
  // No pre-selection — both options shown equally on every open (the key
  // provider-switch invariant: the prior provider is not persisted between
  // open/close cycles).
  const [provider, setProvider] = useState<Provider | null>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  // Cycle-45.1 guard: only dismiss when the press ORIGINATED on the bare
  // backdrop, so a relayout under the finger can't retarget the click.
  const pressOnBackdrop = useRef(false);

  // Move focus into the panel on open; restore it to the trigger on close.
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLElement>(
      'button, input, select, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    return () => restoreRef.current?.focus?.();
  }, []);

  // Escape closes; Tab is trapped within the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const nodes = panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!nodes || nodes.length === 0) return;
        const list = Array.from(nodes);
        const first = list[0]!;
        const last = list[list.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Kiosk: track the on-screen keyboard via focus within the panel and
  // scroll the focused field's labelled group to the top so the field +
  // submit stay visible above the keyboard (cycle-88 hotfix pattern).
  const isKiosk = variant === "kiosk";
  const onFocusCapture = (e: React.FocusEvent) => {
    if (!isKiosk) return;
    const t = e.target as HTMLElement;
    if (t.tagName === "INPUT" || t.tagName === "SELECT") {
      setKeyboardOpen(true);
      const group = t.closest("label") ?? t;
      window.setTimeout(
        () => group.scrollIntoView({ block: "start", behavior: "smooth" }),
        250,
      );
    }
  };
  const onBlurCapture = (e: React.FocusEvent) => {
    if (!isKiosk) return;
    const next = e.relatedTarget as HTMLElement | null;
    if (next && panelRef.current?.contains(next)) return;
    window.setTimeout(() => setKeyboardOpen(false), 100);
  };

  return (
    <div
      className={`${styles.backdrop} ${
        isKiosk && keyboardOpen ? styles.kbOpen : ""
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Connect to CGM"
      onMouseDown={(e) => {
        pressOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressOnBackdrop.current && e.target === e.currentTarget) onClose();
        pressOnBackdrop.current = false;
      }}
    >
      <button
        type="button"
        className={styles.closeBtn}
        aria-label="Close"
        onClick={onClose}
      >
        ✕
      </button>
      <div
        ref={panelRef}
        className={`${styles.scroll} ${provider ? "" : styles.scrollWide}`}
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
      >
        {!provider ? (
          <ProviderPicker onPick={(p) => setProvider(p)} />
        ) : (
          <CGMConnectForm
            provider={provider}
            mode="connect"
            onChangeProvider={() => setProvider(null)}
            onConnected={onConnected}
          />
        )}
      </div>
    </div>
  );
}
